import { useEffect, useState } from 'react';
import CourseMappingAddFilter from './CourseMappingAddFilter';
import CourseMappingFilterChip from './CourseMappingFilterChip';
import {
  COURSE_MAPPING_FILTER_FIELDS,
  CourseMappingFilterOptionsData,
  CourseMappingFilterState,
  CourseMappingFilterValue,
  findFilterField,
  getFilterFieldOptions,
  initialFilterValue,
  isFilterApplied,
} from './courseMappingFilterFields';

import './CourseMappingFilters.css';

interface CourseMappingFiltersProps {
  filters: CourseMappingFilterState;
  optionsData: CourseMappingFilterOptionsData;
  lockedKeys?: string[];
  filterToOpen?: { key: string; nonce: number } | null;
  onFiltersChange: (next: CourseMappingFilterState) => void;
}

const CourseMappingFilters = ({
  filters,
  optionsData,
  lockedKeys = [],
  filterToOpen,
  onFiltersChange,
}: CourseMappingFiltersProps) => {
  const lockedKeySet = new Set(lockedKeys);
  const [justAddedKey, setJustAddedKey] = useState<string | null>(null);

  const activeKeys = new Set(filters.map((entry) => entry.key));
  const availableFields = COURSE_MAPPING_FILTER_FIELDS.filter((field) => !activeKeys.has(field.key));

  const handleAddField = (fieldKey: string) => {
    const field = findFilterField(fieldKey);
    if (!field) {
      return;
    }

    setJustAddedKey(field.key);
    onFiltersChange([...filters, { key: field.key, value: initialFilterValue(field) }]);
  };

  // Opening a filter from the column header: add the chip if missing, otherwise re-open the existing one.
  useEffect(() => {
    if (!filterToOpen || !findFilterField(filterToOpen.key)) {
      return;
    }

    if (activeKeys.has(filterToOpen.key)) {
      setJustAddedKey(filterToOpen.key);
    } else {
      handleAddField(filterToOpen.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterToOpen?.nonce]);

  const handleChipCommit = (fieldKey: string, value: CourseMappingFilterValue) => {
    setJustAddedKey(null);
    const field = findFilterField(fieldKey);

    // Clearing a filter's value removes the chip entirely.
    if (field && !isFilterApplied(field, value)) {
      onFiltersChange(filters.filter((entry) => entry.key !== fieldKey));
      return;
    }

    onFiltersChange(filters.map((entry) => (entry.key === fieldKey ? { key: fieldKey, value } : entry)));
  };

  const handleChipRemove = (fieldKey: string) => {
    setJustAddedKey(null);
    onFiltersChange(filters.filter((entry) => entry.key !== fieldKey));
  };

  return (
    <div className="course-mapping-filters-bar">
      {filters.map((entry) => {
        const field = findFilterField(entry.key);
        if (!field) {
          return null;
        }

        return (
          <CourseMappingFilterChip
            key={field.key}
            field={field}
            value={entry.value}
            options={getFilterFieldOptions(field, optionsData)}
            defaultOpen={justAddedKey === field.key}
            locked={lockedKeySet.has(field.key)}
            onCommit={(value) => handleChipCommit(field.key, value)}
            onRemove={() => handleChipRemove(field.key)}
          />
        );
      })}
      <CourseMappingAddFilter availableFields={availableFields} onSelect={handleAddField} />
    </div>
  );
};

export default CourseMappingFilters;
